/* global ApplePaySession */
import React, { useEffect, useState, useCallback } from "react";
import "./ApplePayButton.module.css";
import { post } from "aws-amplify/api";

async function validateMerchant(
  merchantId,
  appleURL,
  initiativeContext,
  callback,
) {
  try {
    const validation = post({
      apiName: "ApplePayMerchantValidation",
      path: "/validate-merchant",
      options: {
        body: {
          merchantId: merchantId,
          validationURL: appleURL,
          ...(initiativeContext && { initiativeContext }),
        },
      },
    });

    const { body } = await validation.response;
    const response = await body.json();

    const merchantSession = JSON.parse(response.body);
    console.log("Merchant session:", merchantSession);

    callback(merchantSession); // Callback to complete merchant validation
  } catch (e) {
    console.error("Merchant validation failed:", e);
  }
}

const ApplePayButton = ({ handleTokenOutput, applePaySettings }) => {
  const [canUseApplePay, setCanUseApplePay] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if Apple Pay is available in browser
  const checkApplePayAvailability = useCallback(async () => {
    if (window.ApplePaySession) {
      try {
        const canMakePayments = await ApplePaySession.canMakePayments();
        setCanUseApplePay(canMakePayments);
        if (!canMakePayments) {
          console.log("Apple Pay not available");
        }
      } catch (error) {
        console.error("Error loading Apple Pay:", error);
      } finally {
        setLoading(false);
      }
    } else {
      console.log("Apple Pay not supported");
      setLoading(false);
    }
  }, []);

  // Calculate the current date + 1 month
  const getDeferredDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setHours(0, 0, 0, 0); // Set time to 00:00
    return date.toISOString(); // Returns date in ISO format (e.g. "2024-12-01T00:00:00Z")
  };

  // Disbursement logic depending on Payment Request API
  const handleDisbursementRequest = useCallback(async () => {
    try {
      const amount = Number(applePaySettings.amount).toFixed(2);
      const paymentMethodData = [
        {
          supportedMethods: "https://apple.com/apple-pay",
          data: {
            version: 15,
            merchantIdentifier: applePaySettings.merchantId,
            merchantCapabilities: [
              "supports3DS",
              ...(applePaySettings.allowDebit && !applePaySettings.allowCredit
                ? ["supportsDebit"]
                : []),
              ...(applePaySettings.allowCredit && !applePaySettings.allowDebit
                ? ["supportsCredit"]
                : []),
            ],
            supportedNetworks: [
              ...(applePaySettings.allowVisa ? ["visa"] : []),
              ...(applePaySettings.allowMastercard ? ["masterCard"] : []),
            ],
            countryCode: applePaySettings.country,
            merchantCategoryCode: applePaySettings.mcc,
          },
        },
      ];

      const paymentDetails = {
        total: {
          label: "Withdraw Funds",
          amount: {
            value: amount,
            currency: applePaySettings.currency,
          },
        },
        modifiers: [
          {
            supportedMethods: "https://apple.com/apple-pay",
            data: {
              disbursementRequest: {
                requiredRecipientContactFields: ["name", "postalAddress"],
              },
              additionalLineItems: [
                {
                  label: "Total Amount",
                  amount,
                },
                {
                  label: "Account Withdrawal",
                  amount,
                  disbursementLineItemType: "disbursement",
                },
              ],
            },
          },
        ],
      };

      const paymentOptions = {};

      console.log("Payment Method Data: ", paymentMethodData);
      console.log("Payment Details: ", paymentDetails);
      console.log("Payment Options: ", paymentOptions);

      const request = new PaymentRequest(
        paymentMethodData,
        paymentDetails,
        paymentOptions,
      );

      request.onmerchantvalidation = (event) => {
        // Call server to validate the merchant session
        validateMerchant(
          applePaySettings.merchantId,
          event.validationURL,
          applePaySettings.initiativeContext,
          function (merchantSession) {
            event.complete(merchantSession);
          },
        );
      };

      request.onpaymentmethodchange = (event) => {
        event.updateWith({
          total: paymentDetails.total,
          modifiers: paymentDetails.modifiers,
        });
      };

      let response;
      try {
        response = await request.show();
        console.log("Apple Pay Payment Token: ", response);

        handleTokenOutput(response, "Apple Pay Disbursement"); // Push Apple Pay token output to state

        // Process the payout here
        await response.complete("success");
      } catch (err) {
        console.error("Error during request.show():", err);
        throw err;
      }
    } catch (error) {
      console.error("Payment request failed:", error);
    }
  }, [applePaySettings, handleTokenOutput]);

  // Apple Pay button click handler
  const handleApplePay = useCallback(() => {
    if (applePaySettings.paymentType === "disbursement") {
      // Check if Payment Request API supported by browser
      if (PaymentRequest) {
        handleDisbursementRequest();
        return; // Exit early if handling disbursement
      } else {
        console.error("Disbursements not available");
        applePaySettings.paymentType = "final"; // Continue with standard payment
      }
    }

    const supportedNetworks = [
      ...(applePaySettings.allowCb ? ["cartesBancaires"] : []),
      ...(applePaySettings.allowMada ? ["mada"] : []),
      ...(applePaySettings.allowJaywan ? ["jaywan"] : []),
      ...(applePaySettings.allowEftpos ? ["eftpos"] : []),
      ...(applePaySettings.allowVisa ? ["visa"] : []),
      ...(applePaySettings.allowMastercard ? ["masterCard"] : []),
      ...(applePaySettings.allowMaestro ? ["maestro"] : []),
      ...(applePaySettings.allowAmex ? ["amex"] : []),
      ...(applePaySettings.allowDiscover ? ["discover"] : []),
      ...(applePaySettings.allowJcb ? ["jcb"] : []),
      ...(applePaySettings.allowUnionPay ? ["chinaUnionPay"] : []),
    ];

    const merchantLabels = {
      "merchant.sandbox.gateway": "Sandbox Merchant",
      "merchant.sandbox.e2e": "E2E Test Merchant",
      "merchant.china.mainland.rsa": "China Merchant",
      "merchant.qa.stephen": "QA Merchant",
      "merchant.knet.test": "KNET Merchant",
      "merchant.knet.gap.test": "GAP",
      "merchant.qpay.test": "QPay Merchant",
      "merchant.qpay.test2": "QPay Merchant 2",
      "merchant.prod.stephen": "PROD Merchant",
      // "merchant.prod.malpb": "MALPB PROD Merchant",
      "merchant.blue.emi.prod": "Blue EMI PROD Merchant",
      "merchant.test.decryption": "Test Decryption Merchant",
    };

    // MPAN update URLs
    const APPLE_PAY_TOKEN_NOTIFICATION_URL =
      applePaySettings.tokenNotificationURL;
    const APPLE_PAY_MANAGEMENT_URL = applePaySettings.managementURL;

    // Define the base payment request
    let paymentRequest = {
      countryCode: applePaySettings.country,
      currencyCode: applePaySettings.currency,
      merchantCategoryCode: applePaySettings.mcc,
      total: {
        label: merchantLabels[applePaySettings.merchantId] || "Demo Merchant",
        amount: applePaySettings.amount.toString(),
      },
      supportedNetworks,
      merchantCapabilities: [
        "supports3DS",
        ...(applePaySettings.allowUnionPay ? ["supportsEMV"] : []),
        ...(applePaySettings.allowDebit && !applePaySettings.allowCredit
          ? ["supportsDebit"]
          : []),
        ...(applePaySettings.allowCredit && !applePaySettings.allowDebit
          ? ["supportsCredit"]
          : []),
      ],
      ...(applePaySettings.requireBillingContact && {
        requiredBillingContactFields: ["name", "postalAddress"],
      }),
      ...(applePaySettings.requireShippingContact && {
        requiredShippingContactFields: [
          "postalAddress",
          "name",
          "phone",
          "email",
        ],
      }),
      ...(applePaySettings.requireShippingMethod && {
        shippingMethods: [
          {
            identifier: "standard",
            label: "Standard Shipping",
            detail: "Arrives in 5 to 7 days",
            amount: "0.00",
          },
          {
            identifier: "express",
            label: "Express Shipping",
            detail: "Arrives in 1 to 2 days",
            amount: "5.00",
          },
        ],
      }),
    };

    const deferredDate = getDeferredDate();

    if (applePaySettings.paymentType === "recurring") {
      paymentRequest = {
        ...paymentRequest,
        lineItems: [
          {
            label: "1 Month Trial",
            amount: "0.00",
            paymentTiming: "recurring",
            recurringPaymentEndDate: deferredDate,
          },
          {
            label: "Recurring",
            amount: applePaySettings.amount.toString(),
            paymentTiming: "recurring",
            recurringPaymentStartDate: deferredDate,
          },
        ],
        recurringPaymentRequest: {
          paymentDescription: "Recurring Payment",
          regularBilling: {
            label: "Recurring",
            amount: applePaySettings.amount.toString(),
            paymentTiming: "recurring",
            recurringPaymentStartDate: deferredDate,
            recurringPaymentIntervalUnit: "month",
            recurringPaymentIntervalCount: 1, // Monthly payment
          },
          trialBilling: {
            label: "1 Month Trial",
            amount: "0.00",
            paymentTiming: "recurring",
            recurringPaymentEndDate: deferredDate,
          },
          billingAgreement:
            "Payment to be taken in one month's time, then once per month",
          managementURL: APPLE_PAY_MANAGEMENT_URL,
          tokenNotificationURL: APPLE_PAY_TOKEN_NOTIFICATION_URL,
        },
      };
    }
    if (applePaySettings.paymentType === "deferred") {
      paymentRequest = {
        ...paymentRequest,
        deferredPaymentRequest: {
          paymentDescription: "Deferred Payment",
          deferredBilling: {
            label: "Pay Later",
            amount: applePaySettings.amount.toString(),
            paymentTiming: "deferred",
            type: "final",
            deferredPaymentDate: deferredDate,
          },
          freeCancellationDate: deferredDate,
          freeCancellationDateTimeZone: "GMT",
          billingAgreement: "Payment to be taken in one month's time",
          managementURL: APPLE_PAY_MANAGEMENT_URL,
          tokenNotificationURL: APPLE_PAY_TOKEN_NOTIFICATION_URL,
        },
      };
    }
    if (applePaySettings.paymentType === "autoReload") {
      paymentRequest = {
        ...paymentRequest,
        lineItems: [
          {
            label: "Automatic Top-up",
            amount: applePaySettings.amount.toString(),
            paymentTiming: "automaticReload",
            automaticReloadPaymentThresholdAmount: "5.00",
          },
        ],
        automaticReloadPaymentRequest: {
          paymentDescription: "Automatic Reload Payment",
          automaticReloadBilling: {
            label: "Automatic Top-up",
            amount: "15.00",
            paymentTiming: "automaticReload",
            automaticReloadPaymentThresholdAmount: "5.00",
          },
          billingAgreement:
            "Payment to be taken when balance reaches minimum threshold",
          managementURL: APPLE_PAY_MANAGEMENT_URL,
          tokenNotificationURL: APPLE_PAY_TOKEN_NOTIFICATION_URL,
        },
      };
    }

    // Multi-token payments request a payment token per merchant context
    if (
      applePaySettings.multiToken &&
      applePaySettings.multiTokenContexts?.length
    ) {
      const multiTokenContexts = applePaySettings.multiTokenContexts.map(
        (context, index) => ({
          merchantIdentifier: context.merchantId,
          externalIdentifier: context.merchantId,
          merchantName: merchantLabels[context.merchantId] || "Demo Merchant",
          merchantDomain: applePaySettings.initiativeContext || "wallets-test-suite.cko.solutions",
          amount: (Number(context.amount) || 0).toFixed(2),
        }),
      );

      paymentRequest = {
        ...paymentRequest,
        lineItems: multiTokenContexts.map((context) => ({
          label: `Item from ${context.merchantName}`,
          amount: context.amount,
        })),
        multiTokenContexts,
        total: {
          ...paymentRequest.total,
          // Keep the total in step with the individual merchant amounts
          amount: multiTokenContexts
            .reduce((total, context) => total + Number(context.amount), 0)
            .toFixed(2),
        },
      };
    }

    console.log("Payment Request: ", paymentRequest);

    const session = new ApplePaySession(15, paymentRequest);

    session.onvalidatemerchant = (event) => {
      // Call server to validate the merchant session
      validateMerchant(
        applePaySettings.merchantId,
        event.validationURL,
        applePaySettings.initiativeContext,
        function (merchantSession) {
          session.completeMerchantValidation(merchantSession);
        },
      );
    };

    if (applePaySettings.requireShippingMethod) {
      session.onshippingmethodselected = (event) => {
        const shipping = event.shippingMethod;
        const baseAmount = Number(applePaySettings.amount);
        const shippingAmount = Number(shipping.amount);
        const total = (baseAmount + shippingAmount).toFixed(2);
        const merchantLabel =
          merchantLabels[applePaySettings.merchantId] || "Demo Merchant";
        session.completeShippingMethodSelection(
          ApplePaySession.STATUS_SUCCESS,
          { label: merchantLabel, amount: total },
          [
            { label: "Subtotal", amount: baseAmount.toFixed(2) },
            { label: shipping.label, amount: shipping.amount },
          ],
        );
      };
    }

    session.onpaymentauthorized = (event) => {
      handleTokenOutput(event, "Apple Pay"); // Push Apple Pay token output to state

      // Process the payment here
      session.completePayment(ApplePaySession.STATUS_SUCCESS);
    };

    session.begin();
  }, [applePaySettings, handleTokenOutput, handleDisbursementRequest]);

  // Check Apple Pay availability on load
  useEffect(() => {
    checkApplePayAvailability();
  }, [checkApplePayAvailability]);

  useEffect(() => {
    const applePayButton = document.querySelector("apple-pay-button");

    if (applePayButton && canUseApplePay) {
      // Bind click event to handleApplePay
      applePayButton.addEventListener("click", handleApplePay);

      // Cleanup function to remove event listener
      return () => {
        applePayButton.removeEventListener("click", handleApplePay);
      };
    }
  }, [canUseApplePay, handleApplePay, applePaySettings]);

  // Ensure the button is re-rendered if it doesn't appear initially
  useEffect(() => {
    if (canUseApplePay && !document.querySelector("apple-pay-button")) {
      setCanUseApplePay(false);
      setTimeout(() => setCanUseApplePay(true), 0);
    }
  }, [canUseApplePay]);

  return (
    <>
      {loading ? (
        <div
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        ></div>
      ) : canUseApplePay ? (
        <div
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        >
          <apple-pay-button
            type={applePaySettings.buttonType}
            buttonstyle={applePaySettings.buttonStyle}
            locale={applePaySettings.buttonLanguage}
          ></apple-pay-button>
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export default ApplePayButton;
